// F-1 · LiftDetailCard — accordion card per lift in the creator page
// (blueprint v2 Phase F). Scenario picker S1–S6, geometry form + embedded
// LiftDiagramPanel, hardware incl. role-tagged equipment links
// (LiftHardware.equipment[] assetId+role+wllLbs — §8.8 accommodation; the
// picker lists EXISTING company assets via assetApi — no new asset module),
// loading incl. DLF, computed panel with ShownWork, complianceRefs,
// per-lift sign-off.
//
// computed is NEVER read from storage here: the parent recompute()s on every
// render/load and passes the fresh result down; engineVersion mismatch forces
// recalculation in recomputeComputed (see utils/recomputeLift.ts).
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Trash2, Plus, PenLine } from 'lucide-react';
import type {
  AnchorVehicle,
  LiftDetail,
  LiftComputed,
  LiftGeometry,
  LiftHardware,
  LiftLoading,
  Scenario,
  StructureType,
} from '@/types/planData';
import { ShownWork } from '@/components/rigging/ShownWork';
import { LiftDiagramPanel } from '@/components/rigging/LiftDiagramPanel';
import { assetApi } from '@/utils/api';
import type { Asset } from '@/types';
import {
  CONTAINER_TYPES,
  computedForceForRole,
  distinctContainers,
  filterAssetsByContainers,
  inspectionBadge,
  inspectionDisplay,
  utilizationBand,
  utilizationColor,
  WLL_UNKNOWN_MESSAGE,
  assetsForRole,
  type ContainerRef,
  type EquipmentContainerType,
  type UsabilityVerdict,
} from './equipmentPicker';
import type { EquipmentInspectionSnapshotEntry } from '@/types/planData';

// §8.8 Qwen fix 4 — shared short-TTL verdict cache (module level, so N
// LiftDetailCard instances share one batch fetch instead of N redundant calls).
// Exported so the parent page can invalidate after equipment changes.
const EQUIPMENT_CACHE_TTL_MS = 30_000;
// Qwen round-2 NEW-1: the cache is keyed by companyId so a fast logout→login as
// a DIFFERENT company inside the TTL window can never surface the prior
// company's asset names/verdicts in the picker. (Same identity source as
// initialReportQueue.queueStorageKey: localStorage 'user' carries companyId.)
function equipmentCacheKey(): string {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return Number.isSafeInteger(user?.companyId) ? String(user.companyId) : 'anon';
  } catch { return 'anon'; }
}
const equipmentCache: Record<string, { val: { assets: Asset[]; verdicts: Record<string, UsabilityVerdict>; at: number } | null }> = {};
const cacheListeners = new Set<() => void>();
export function invalidateEquipmentCache(): void {
  for (const k of Object.keys(equipmentCache)) delete equipmentCache[k];
  for (const fn of cacheListeners) fn();
}

export const SCENARIOS: { value: Scenario; label: string }[] = [
  // Brian 2026-09-09: only S1 (top only) and S2 (top + heel) make sense as
  // picker options today. Jump block is for real gin poles; the old S4/S6
  // were tag-block arrangements; redirect becomes an ADD-ON block later
  // (top + redirect, top + redirect + heel). Engine keeps all scenario math.
  { value: 'S1-top-only', label: 'S1 — Top block only' },
  { value: 'S2-top-heel', label: 'S2 — Top + heel block' },
];

const ROLES: LiftHardware['equipment'][number]['role'][] = [
  'hoist-line', 'tag-line', 'static-line', 'sling', 'block', 'shackle',
];

export interface LiftDetailCardProps {
  lift: LiftDetail;
  index: number;
  computed: LiftComputed | null;
  onChange: (lift: LiftDetail) => void;
  readOnly?: boolean;
  canRemove?: boolean;
  onRemove?: () => void;
  equipmentTable?: React.ReactNode;
  // §8.8 #8: plan-header container scope ('Equipment source: truck/trailer/crew').
  // Optional controlled state — uncontrolled local state when the plan header
  // doesn't own the scope (keeps LiftDetailCard standalone for other callers).
  containers?: EquipmentContainerType[];
  onContainersChange?: (next: EquipmentContainerType[]) => void;
  // WD-T6 / v1.4 #1: the plan's ANCHOR TRUCK (capstan winch attaches to it).
  // Optional so the card stays standalone; the plan header owns the choice.
  anchorVehicle?: AnchorVehicle | null;
  // UAT-015: point-in-time inspection snapshot captured at plan save. Used as
  // the badge fallback when the live usability fetch is unavailable or lacks
  // the asset — the saved plan keeps what the picker KNEW at save time.
  verdictSnapshot?: Record<string, EquipmentInspectionSnapshotEntry>;
  // UAT-015: called when this card loads assets + usability verdicts so the
  // plan create page can snapshot them into the save payload.
  onVerdictsLoaded?: (assets: Asset[], verdicts: Record<string, UsabilityVerdict>) => void;
}

const inputCls = 'w-full px-2 py-1.5 border border-gray-300 rounded text-sm';

function NumField({
  label, value, onChange, step = 'any', disabled, id,
}: {
  label: string; value: number; onChange: (v: number) => void; step?: string; disabled?: boolean; id?: string;
}) {
  // Raw-string input (Brian 2026-09-09): no leading-zero prefixing. Zero shows
  // as EMPTY; typing goes into an empty box. Commits on change; empty = 0.
  const [raw, setRaw] = useState(value ? String(value) : '');
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setRaw(value ? String(value) : '');
  }, [value, focused]);
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        value={raw}
        disabled={disabled}
        placeholder="0"
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); const n = parseFloat(raw); onChange(Number.isFinite(n) ? n : 0); }}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '' || /^-?\d*\.?\d*$/.test(v)) {
            setRaw(v);
            const n = parseFloat(v);
            onChange(Number.isFinite(n) ? n : 0);
          }
        }}
        className={inputCls}
      />
    </div>
  );
}

export function LiftDetailCard({
  lift, index, computed, onChange, readOnly = false, canRemove = true, onRemove, equipmentTable,
  containers: containersProp, onContainersChange, anchorVehicle = null,
  verdictSnapshot, onVerdictsLoaded,
}: LiftDetailCardProps) {
  const [open, setOpen] = useState(index === 0);
  const [showMath, setShowMath] = useState(false);
  const [assets, setAssets] = useState<Asset[]>([]);
  // UAT-014: one unified selector — the role select feeds BOTH the gated
  // picker's type-compatibility filter and manual entry; the flat duplicate
  // asset list is gone.
  const [newRole, setNewRole] = useState<LiftHardware['equipment'][number]['role']>('sling');
  const [manualEntry, setManualEntry] = useState(false);
  const [manualLabel, setManualLabel] = useState('');
  const [newWll, setNewWll] = useState<string>('');
  const [newRef, setNewRef] = useState('');
  const [signerName, setSignerName] = useState('');
  // §8.8 equipment picker: container scope + live usability verdicts. Manual
  // entry below stays untouched — the picker is additive ('Select from
  // equipment'), zero-sticker workflow preserved.
  const [localContainers, setLocalContainers] = useState<EquipmentContainerType[]>([]);
  const containers = containersProp ?? localContainers;
  const setContainers = (next: EquipmentContainerType[]) => {
    setLocalContainers(next);
    onContainersChange?.(next);
  };
  // S2-LUNA-DELTA-001 (2026-09-30): "which specific container" scope — the
  // kind checkboxes alone let gear from ANOTHER truck/crew into the picker.
  // Candidates derive from the loaded asset list (pointer-based, label
  // fallback); local UI scope only (the plan schema still stores kinds).
  const [scopeKey, setScopeKey] = useState('');
  const containerCandidates = distinctContainers(assets, containers);
  const scopeCandidate = containerCandidates.find((c) => c.key === scopeKey);
  useEffect(() => {
    // A stale specific scope must not silently persist when its kind is
    // unchecked or its container disappears from the list.
    if (scopeKey && !containerCandidates.some((c) => c.key === scopeKey)) setScopeKey('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containers, assets]);
  const scopeLabels = scopeCandidate ? [scopeCandidate.label] : undefined;
  const scopeRefs: ContainerRef[] | undefined =
    scopeCandidate?.refType && scopeCandidate.refId != null
      ? [{ refType: scopeCandidate.refType, refId: scopeCandidate.refId }]
      : undefined;
  const [verdicts, setVerdicts] = useState<Record<string, UsabilityVerdict>>({});
  // §8.8 Qwen fix 2 (HIGH) — fail CLOSED: until verdicts are loaded, equipment
  // options are disabled ('inspection status unavailable'); an API error never
  // re-enables them. One automatic retry fires on error; manual entry stays
  // available (zero-sticker preserved).
  const [verdictsLoaded, setVerdictsLoaded] = useState(false);
  const [verdictsError, setVerdictsError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  // Cache invalidation subscription — re-fires the fetch effect when the
  // parent (or another card) invalidates the shared cache (Qwen fix 4).
  const [cacheEpoch, setCacheEpoch] = useState(0);
  const retryAttemptRef = useRef(0); // max one automatic retry (Qwen fix 2)
  useEffect(() => {
    const bump = () => { setVerdictsLoaded(false); retryAttemptRef.current = 0; setCacheEpoch((e) => e + 1); };
    cacheListeners.add(bump);
    return () => { cacheListeners.delete(bump); };
  }, []);

  useEffect(() => {
    // UAT-015: fetch on MOUNT, not only when the card is open — the saved
    // plan's inspection badges must reflect reality without the reader having
    // to expand each lift. (The batch is module-cached, so N cards still cost
    // ONE fetch per TTL window.)
    // §8.8 Qwen fix 4 — module-level short-TTL cache shared across all
    // LiftDetailCard instances: N cards fire ONE batch call per TTL window
    // instead of N redundant calls, and data is REFETCHED whenever the cache
    // is stale (not "only when the local list is empty" — stale verdicts are
    // how a tagged-out asset slips through). invalidateEquipmentCache()
    // forces the next open to refetch (e.g. after saving a truck kit).
    const cached = equipmentCache[equipmentCacheKey()]?.val;
    if (cached && Date.now() - cached.at < EQUIPMENT_CACHE_TTL_MS) {
      setAssets(cached.assets);
      setVerdicts(cached.verdicts);
      setVerdictsLoaded(true);
      setVerdictsError(null);
      return undefined;
    }
    let cancelled = false;
    assetApi
      .getAll()
      .then(async (res) => {
        if (cancelled) return;
        const list = res.data ?? [];
        setAssets(list);
        if (list.length === 0) {
          setVerdicts({});
          setVerdictsLoaded(true);
          return;
        }
        try {
          const next = await assetApi.getUsability(list.map((a) => a.id));
          if (cancelled) return;
          setVerdicts(next);
          setVerdictsLoaded(true);
          setVerdictsError(null);
          retryAttemptRef.current = 0;
          const cacheSlot = equipmentCache[equipmentCacheKey()] ?? (equipmentCache[equipmentCacheKey()] = { val: null });
          cacheSlot.val = { assets: list, verdicts: next, at: Date.now() };
        } catch {
          if (cancelled) return;
          // Fail closed: loaded stays false → options stay disabled.
          setVerdictsLoaded(false);
          setVerdictsError('Inspection status unavailable — cannot verify the §4.5 inspection gate.');
          // Automatic retry once (Qwen fix 2) — refire the fetch effect.
          if (retryAttemptRef.current < 1) {
            retryAttemptRef.current += 1;
            setCacheEpoch((e) => e + 1);
          }
        }
      })
      .catch(() => {
        if (cancelled) return;
        setVerdictsLoaded(false);
        setVerdictsError('Inspection status unavailable — cannot verify the §4.5 inspection gate.');
        if (retryAttemptRef.current < 1) {
          retryAttemptRef.current += 1;
          setCacheEpoch((e) => e + 1);
        }
      });
    return () => { cancelled = true; };
  }, [cacheEpoch]);

  // UAT-015: hand the freshly loaded assets+verdicts to the parent so the
  // plan save can snapshot the point-in-time inspection state.
  useEffect(() => {
    if (!verdictsLoaded || !onVerdictsLoaded) return undefined;
    onVerdictsLoaded(assets, verdicts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verdictsLoaded, assets, verdicts]);

  const g = lift.geometry;
  const setGeometry = (patch: Partial<LiftGeometry>) =>
    onChange({ ...lift, geometry: { ...lift.geometry, ...patch } });
  const setHardware = (patch: Partial<LiftHardware>) =>
    onChange({ ...lift, hardware: { ...lift.hardware, ...patch } });
  const setLoading = (patch: Partial<LiftLoading>) =>
    onChange({ ...lift, loading: { ...lift.loading, ...patch } });

  const changeScenario = (scenario: Scenario) => {
    // Scenario switch shapes geometry additively — existing values preserved.
    const next: LiftGeometry = { ...g };
    if (scenario === 'S1-top-only') next.heelBlock = null;
    if (scenario === 'S2-top-heel' && !next.heelBlock) {
      next.heelBlock = { heightFt: 10, offsetFromTowerFt: 1, attachment: { method: 'poleShaft' } };
    }
    if (scenario === 'S3-jump' && !next.jumpBlock) {
      next.jumpBlock = { heightFt: Math.round(g.structureHeightFt / 2), side: 'A', offsetFromCenterFt: 2, attachment: { method: 'legPanelPoint' } };
    }
    if (scenario === 'S4-tied-back') {
      if (!next.tieBackAnchor) next.tieBackAnchor = { heightFt: g.structureHeightFt };
      if (!next.mountBlock) next.mountBlock = { heightFt: Math.round(g.structureHeightFt * 0.8), offsetFt: 2, attachment: { method: 'antennaMount', antennaMountVariant: 'primaryBlockOnStructure' } };
    }
    if (scenario === 'S5-redirect' && !next.redirectBlock) {
      next.redirectBlock = { heightFt: Math.round(g.structureHeightFt * 0.8), offsetFt: 2, attachment: { method: 'legPanelPoint' } };
    }
    if (scenario === 'S6-highline-trolley' && !next.highline) {
      next.highline = { shaftAnchor: { heightFt: Math.round(g.structureHeightFt * 0.9), offsetFt: 1 }, trolleyPositionFt: 0.5 };
    }
    onChange({ ...lift, scenario, geometry: next });
  };

  const changeStructureType = (structureType: StructureType) => setGeometry({ structureType });

  // UAT-014: manual (zero-sticker) entry — a row with NO assetId, recorded
  // honestly as "manual" with a description and WLL. The §4.5 usability gate
  // skips manual rows server-side (no asset to gate).
  const addManualEquipment = () => {
    const label = manualLabel.trim();
    if (!label) return;
    setHardware({
      equipment: [
        ...lift.hardware.equipment,
        { label, role: newRole, wllLbs: parseFloat(newWll) || 0 },
      ],
    });
    setManualLabel('');
    setNewWll('');
  };

  const removeEquipmentLink = (i: number) =>
    setHardware({ equipment: lift.hardware.equipment.filter((_, j) => j !== i) });

  // §8.8 picker state: selected asset id + surfaced block reason. Blocked
  // gear CANNOT be added — hard block with the §4.5 reason shown (§8.8 #3).
  const [pickAssetId, setPickAssetId] = useState<string>('');
  const [pickBlockReason, setPickBlockReason] = useState<string | null>(null);

  const pickEquipment = () => {
    const id = parseInt(pickAssetId, 10);
    if (!Number.isFinite(id)) return;
    // Fail closed: refuse to add gear while verdicts are not loaded — an API
    // error or slow load must never make blocked gear selectable (§8.8 #3).
    if (!verdictsLoaded) {
      setPickBlockReason('Inspection status unavailable — cannot verify §4.5 gate. Try again in a moment.');
      return;
    }
    const verdict = verdicts[String(id)];
    if (verdict && !verdict.usable) {
      // Hard block with reason — never silently dropped (§8.8 #3).
      setPickBlockReason(verdict.reason ?? 'Blocked — out of service.');
      return;
    }
    const asset = assets.find((a) => a.id === id);
    setPickBlockReason(null);
    // WLL comes from the asset record (§8.8 #5) — 0 means unknown → amber
    // INCOMPLETE on the row; never invented.
    setHardware({
      equipment: [
        ...lift.hardware.equipment,
        { assetId: id, role: newRole, wllLbs: asset?.wllLbs != null ? Number(asset.wllLbs) : 0 },
      ],
    });
    setPickAssetId('');
  };

  const addComplianceRef = () => {
    const ref = newRef.trim();
    if (!ref || lift.complianceRefs.includes(ref)) return;
    onChange({ ...lift, complianceRefs: [...lift.complianceRefs, ref] });
    setNewRef('');
  };

  const signLift = () => {
    if (!signerName.trim()) return;
    onChange({ ...lift, signOff: { reviewedBy: signerName.trim(), at: new Date().toISOString() } });
    setSignerName('');
  };

  const refused = computed?.status === 'refused';

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-4" data-testid={`lift-card-${lift.id}`}>
      {/* Header: a real <button> toggles the card; remove-lift is a sibling
          button (NEVER nested inside another button — invalid HTML that broke
          mobile taps). Luna audit 2026-09-09. */}
      <div className="w-full px-6 py-4 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className="flex-1 flex items-center justify-between gap-3 text-left"
        >
          <div className="flex items-center gap-3">
            <span className="font-semibold text-gray-900">
              Lift {index + 1} — {lift.name}
            </span>
            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
              {lift.scenario}
            </span>
            {refused && (
              <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-semibold rounded-full">REFUSED</span>
            )}
            {lift.signOff.reviewedBy && (
              <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                reviewed by {lift.signOff.reviewedBy}
              </span>
            )}
          </div>
          {open ? <ChevronUp className="h-5 w-5 text-gray-500 shrink-0" /> : <ChevronDown className="h-5 w-5 text-gray-500 shrink-0" />}
        </button>
        {canRemove && !readOnly && onRemove && (
          <button
            type="button"
            aria-label={`Remove ${lift.name}`}
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="text-red-500 hover:text-red-700 ml-2 shrink-0"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && (
        <div className="p-6 bg-white" data-testid={`lift-card-body-${lift.id}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* LEFT: inputs */}
            <div className="space-y-4">
              <h4 className="font-bold text-gray-900 border-b pb-2">SCENARIO & GEOMETRY</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Lift Name</label>
                  <input
                    type="text"
                    value={lift.name}
                    disabled={readOnly}
                    onChange={(e) => onChange({ ...lift, name: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Scenario</label>
                  <select
                    value={lift.scenario}
                    disabled={readOnly}
                    onChange={(e) => changeScenario(e.target.value as Scenario)}
                    className={inputCls}
                    data-testid={`scenario-picker-${lift.id}`}
                  >
                    {SCENARIOS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Structure Type</label>
                  <select
                    value={g.structureType}
                    disabled={readOnly}
                    onChange={(e) => changeStructureType(e.target.value as StructureType)}
                    className={inputCls}
                  >
                    <option value="guyed">Guyed</option>
                    <option value="self-support">Self-support</option>
                    <option value="monopole">Monopole</option>
                    <option value="mast-pipe">Mast pipe</option>
                  </select>
                </div>
                <NumField label="Structure Height (ft)" value={g.structureHeightFt} disabled={readOnly}
                  onChange={(v) => setGeometry({ structureHeightFt: v, highestAttachmentFt: Math.max(v, g.highestAttachmentFt) })} />
                <NumField label="Top Block Height (ft)" value={g.topBlock.heightFt} disabled={readOnly}
                  onChange={(v) => setGeometry({ topBlock: { ...g.topBlock, heightFt: v }, highestAttachmentFt: Math.max(v, g.heelBlock?.heightFt ?? 0) })} />
                {/* Block attachment drives a DERIVED offset (Brian 2026-09-08):
                    leg/panel point = 0; face basket = half the face width;
                    antenna mount / mast pipe ≈ 5 ft off the face (editable). */}
                <div>
                  <label htmlFor={`top-attachment-${lift.id}`} className="block text-xs font-medium text-gray-600 mb-1">Block Attachment</label>
                  <select
                    id={`top-attachment-${lift.id}`}
                    value={g.topBlock.attachment?.method ?? 'legPanelPoint'}
                    disabled={readOnly}
                    onChange={(e) => {
                      const method = e.target.value as 'legPanelPoint' | 'faceBasket2' | 'antennaMount' | 'mastPipeRoosterHead';
                      const derived = method === 'faceBasket2'
                        ? (g.faceWidthFt ?? 4) / 2
                        : method === 'antennaMount' || method === 'mastPipeRoosterHead'
                          ? (g.topBlock.attachment?.method === method ? g.topBlock.offsetFromCenterFt : 5)
                          : 0;
                      setGeometry({ topBlock: { ...g.topBlock, offsetFromCenterFt: derived, attachment: { ...g.topBlock.attachment, method } as any } });
                    }}
                    className={inputCls}
                  >
                    <option value="legPanelPoint">Leg at panel point (block at the leg)</option>
                    <option value="faceBasket2">Face basket — 2 slings, block mid-face</option>
                    <option value="antennaMount">Antenna mount (V/T-boom)</option>
                    <option value="mastPipeRoosterHead">Mast pipe / rooster head</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Offset from tower center is derived: panel point = 0 · face basket = half the
                    face width · mount / mast pipe ≈ 5 ft (average leg-to-V-boom-face distance).
                  </p>
                </div>
                {(g.topBlock.attachment?.method === 'antennaMount' || g.topBlock.attachment?.method === 'mastPipeRoosterHead') && (
                  <NumField label="Mount Offset (ft, derived ≈5 — adjust if this mount differs)" value={g.topBlock.offsetFromCenterFt} disabled={readOnly}
                    onChange={(v) => setGeometry({ topBlock: { ...g.topBlock, offsetFromCenterFt: v } })} />
                )}
                {lift.scenario === 'S2-top-heel' && g.heelBlock && (
                  <>
                    <NumField label="Heel Block Height (ft)" value={g.heelBlock.heightFt} disabled={readOnly}
                      onChange={(v) => setGeometry({ heelBlock: { ...g.heelBlock!, heightFt: v } })} />
                    <NumField label="Heel Block Distance (ft)" value={g.heelBlock.offsetFromTowerFt} disabled={readOnly}
                      onChange={(v) => setGeometry({ heelBlock: { ...g.heelBlock!, offsetFromTowerFt: v } })} />
                  </>
                )}
                {lift.scenario === 'S3-jump' && g.jumpBlock && (
                  <>
                    <NumField label="Jump Block Height (ft)" value={g.jumpBlock.heightFt} disabled={readOnly}
                      onChange={(v) => setGeometry({ jumpBlock: { ...g.jumpBlock!, heightFt: v } })} />
                    <NumField label="Jump Block Offset (ft)" value={g.jumpBlock.offsetFromCenterFt} disabled={readOnly}
                      onChange={(v) => setGeometry({ jumpBlock: { ...g.jumpBlock!, offsetFromCenterFt: v } })} />
                  </>
                )}
                {lift.scenario === 'S4-tied-back' && g.tieBackAnchor && (
                  <NumField label="Tie-Back Anchor Height (ft)" value={g.tieBackAnchor.heightFt} disabled={readOnly}
                    onChange={(v) => setGeometry({ tieBackAnchor: { heightFt: v } })} />
                )}
                {lift.scenario === 'S5-redirect' && g.redirectBlock && (
                  <>
                    <NumField label="Redirect Height (ft)" value={g.redirectBlock.heightFt} disabled={readOnly}
                      onChange={(v) => setGeometry({ redirectBlock: { ...g.redirectBlock!, heightFt: v } })} />
                    <NumField label="Redirect Offset (ft)" value={g.redirectBlock.offsetFt} disabled={readOnly}
                      onChange={(v) => setGeometry({ redirectBlock: { ...g.redirectBlock!, offsetFt: v } })} />
                  </>
                )}
                {lift.scenario === 'S6-highline-trolley' && g.highline && (
                  <>
                    <NumField label="Shaft Anchor Height (ft)" value={g.highline.shaftAnchor.heightFt} disabled={readOnly}
                      onChange={(v) => setGeometry({ highline: { ...g.highline!, shaftAnchor: { ...g.highline!.shaftAnchor, heightFt: v } } })} />
                    <NumField label="Trolley Position (0–1)" value={g.highline.trolleyPositionFt} disabled={readOnly}
                      onChange={(v) => setGeometry({ highline: { ...g.highline!, trolleyPositionFt: Math.min(1, Math.max(0, v)) } })} />
                  </>
                )}
                <NumField label="Winch Distance (ft)" value={g.winch.distanceFromTowerFt} disabled={readOnly}
                  onChange={(v) => setGeometry({ winch: { ...g.winch, distanceFromTowerFt: v } })} />
                <NumField label="Load Height (ft)" value={g.load.heightFt} disabled={readOnly}
                  onChange={(v) => setGeometry({ load: { ...g.load, heightFt: v } })} />
                <NumField label="Load Distance (ft)" value={g.load.distanceFromTowerFt} disabled={readOnly}
                  onChange={(v) => setGeometry({ load: { ...g.load, distanceFromTowerFt: v } })} />
                <NumField label="Tag Distance (ft)" value={g.tag.anchorDistanceFt ?? 0} disabled={readOnly}
                  onChange={(v) => setGeometry({ tag: { ...g.tag, anchorDistanceFt: v } })} />
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Tag Method</label>
                  <select
                    value={g.tag.method === 'self' ? 'selfTrolley' : g.tag.method}
                    disabled={readOnly}
                    onChange={(e) => setGeometry({ tag: { ...g.tag, method: e.target.value as LiftGeometry['tag']['method'] } })}
                    className={inputCls}
                  >
                    <option value="straight">Straight tag — separate tag line to a ground hold point</option>
                    <option value="selfTrolley">Self tag (trolley) — load rides the load line on a trolley block</option>
                    <option value="staticTrolley">Static trolley tag — separate static line, trolley rides it</option>
                  </select>
                </div>
              </div>

              <h4 className="font-bold text-gray-900 border-b pb-2">LOADING</h4>
              <div className="grid grid-cols-2 gap-3">
                <NumField label="Gross Load (lbs)" value={lift.loading.grossLoadLbs} disabled={readOnly}
                  onChange={(v) => setLoading({ grossLoadLbs: v })} />
                <NumField label="Dynamic Load Factor (DLF)" value={lift.loading.dynamicFactor} step="0.05" disabled={readOnly}
                  onChange={(v) => setLoading({ dynamicFactor: Math.min(2, Math.max(1, v)) })} />
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={lift.loading.accountsForFriction}
                    disabled={readOnly}
                    onChange={(e) => setLoading({ accountsForFriction: e.target.checked })}
                    className="rounded"
                  />
                  <label className="text-xs font-medium text-gray-600">Accounts for Friction?</label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={lift.personnelLift}
                    disabled={readOnly}
                    onChange={(e) => onChange({ ...lift, personnelLift: e.target.checked })}
                    className="rounded"
                    data-testid={`personnel-lift-${lift.id}`}
                  />
                  <label className="text-xs font-medium text-gray-600">Personnel Lift? (advisory — §8.1)</label>
                </div>
              </div>

              <h4 className="font-bold text-gray-900 border-b pb-2">HARDWARE</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Line Parts (A-13(e))</label>
                  <select
                    value={lift.hardware.blockParts}
                    disabled={readOnly}
                    onChange={(e) => setHardware({ blockParts: parseInt(e.target.value) as LiftHardware['blockParts'] })}
                    className={inputCls}
                  >
                    {/* Form consolidation: capped at 2 parts max (engine math unchanged). */}
                    {[1, 2].map((n) => <option key={n} value={n}>{n}-Part</option>)}
                  </select>
                  {lift.hardware.blockParts === 2 && (
                    <p className="text-xs text-amber-700 mt-1">
                      2-part reeving halves the winch pull but does NOT increase safe lifting capacity —
                      rope, capstan, blocks, and the tower attachment are each still governed by their own WLL.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Sheave Bearing (A-13(i))</label>
                  <select
                    value={lift.hardware.sheaveBearing}
                    disabled={readOnly}
                    onChange={(e) => setHardware({ sheaveBearing: e.target.value as LiftHardware['sheaveBearing'] })}
                    className={inputCls}
                  >
                    <option value="plain">Plain (PB)</option>
                    <option value="roller">Roller (BB/SRB)</option>
                  </select>
                </div>
                <NumField label="Reeved Sheaves" value={lift.hardware.sheaveCount} disabled={readOnly}
                  onChange={(v) => setHardware({ sheaveCount: Math.round(v) })} />
                <NumField label="Rope Diameter (in)" value={lift.hardware.ropeDiameterIn} disabled={readOnly}
                  onChange={(v) => setHardware({ ropeDiameterIn: v })} />
              </div>

              {/* Role-tagged equipment links (§8.8 accommodation — existing assets only) */}
              <div className="border border-gray-200 rounded p-3" data-testid={`equipment-links-${lift.id}`}>
                <p className="text-xs font-semibold text-gray-700 mb-2">
                  Equipment Links (asset + role + WLL — §8.8)
                </p>
                {lift.hardware.equipment.map((eq, i) => {
                  const asset = eq.assetId != null ? assets.find((a) => a.id === eq.assetId) : undefined;
                  const verdict = eq.assetId != null ? verdicts[String(eq.assetId)] : undefined;
                  const snap = eq.assetId != null ? verdictSnapshot?.[String(eq.assetId)] : undefined;
                  // UAT-015: live verdict wins; when unavailable, fall back to
                  // the snapshot captured at plan save — the badge is never a
                  // silent "unknown" when the plan recorded the state.
                  const liveDisplay = verdictsLoaded ? inspectionDisplay(verdict, asset) : null;
                  const fromSnapshot = liveDisplay == null && !!snap;
                  const display = liveDisplay ?? snap?.display ?? 'unknown';
                  const badge = inspectionBadge(display);
                  const force = computedForceForRole(eq.role, computed);
                  const util = utilizationBand(force ?? 0, eq.wllLbs > 0 ? eq.wllLbs : null);
                  const forceLabel = force != null && force > 0
                    ? `${Math.round(force)} lb force`
                    : 'force N/A';
                  return (
                    <div key={`${eq.assetId ?? 'manual'}-${i}`} className="flex items-center gap-2 text-sm mb-1 flex-wrap">
                      <span className="font-mono text-xs text-gray-500">{eq.assetId != null ? `#${eq.assetId}` : 'manual'}</span>
                      {eq.label && <span className="text-gray-800 text-xs font-medium">{eq.label}</span>}
                      <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{eq.role}</span>
                      <span className="text-gray-700">{eq.wllLbs > 0 ? `${eq.wllLbs} lb WLL` : WLL_UNKNOWN_MESSAGE}</span>
                      <span className="text-gray-500 text-xs" data-testid={`equipment-force-${lift.id}-${i}`}>{forceLabel}</span>
                      {eq.wllLbs <= 0 && <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-xs">⚠ INCOMPLETE</span>}
                      <span className={`px-1.5 py-0.5 rounded text-xs ${badge.cls}`}>
                        {badge.icon} {badge.label}{fromSnapshot ? ' (at plan save)' : ''}
                      </span>
                      {display === 'unknown' && (
                        <span className="text-xs text-amber-700">
                          — inspection status not recorded at save; open the plan creator and re-save to record it.
                        </span>
                      )}
                      <span className={`text-xs font-semibold ${utilizationColor(util.band)}`}>
                        {util.percent != null ? `${util.percent.toFixed(0)}% of WLL` : util.band === 'incomplete' ? 'WLL UNKNOWN' : ''}
                      </span>
                      {!readOnly && (
                        <button onClick={() => removeEquipmentLink(i)} className="text-red-500 hover:text-red-700 ml-auto">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
                {!readOnly && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    {/* UAT-014 (unified selector): the ONE role/type-compatible,
                        usability-gated asset picker. Zero-sticker manual entry
                        (no asset) stays available below it. */}
                    <div className="flex items-center gap-2 mt-2" data-testid={`manual-equipment-entry-${lift.id}`}>
                      <label className="flex items-center gap-1 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={manualEntry}
                          onChange={(e) => setManualEntry(e.target.checked)}
                          className="rounded"
                        />
                        Manual entry (no asset — zero-sticker)
                      </label>
                    </div>
                    {manualEntry && (
                      <div className="flex items-center gap-2 mt-2">
                        <select
                          value={newRole}
                          onChange={(e) => setNewRole(e.target.value as typeof newRole)}
                          className="px-1 py-1 border rounded text-xs"
                          aria-label="Manual equipment role"
                        >
                          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                        <input
                          type="text" placeholder="Description (e.g. 1/2&quot; rope tag line)" value={manualLabel}
                          onChange={(e) => setManualLabel(e.target.value)}
                          className="flex-1 px-1 py-1 border rounded text-xs"
                          aria-label="Manual equipment description"
                        />
                        <input
                          type="number" placeholder="WLL lb" value={newWll}
                          onChange={(e) => setNewWll(e.target.value)}
                          className="w-24 px-1 py-1 border rounded text-xs"
                          aria-label="Manual equipment WLL"
                        />
                        <button onClick={addManualEquipment} className="p-1 text-blue-600 hover:text-blue-800" aria-label="Add manual equipment link">
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {/* §8.8 #3 'Select from equipment' picker — container-scoped,
                    live inspection status, BLOCKED gear hard-blocked with
                    reason. Additive: the manual row above stays for zero-sticker. */}
                {!readOnly && (
                  <div className="mt-3 pt-3 border-t border-gray-100" data-testid={`equipment-picker-${lift.id}`}>
                    <p className="text-xs font-semibold text-gray-700 mb-1">
                      Select from equipment (§8.8 — blocked gear cannot be selected)
                    </p>
                    <div className="flex flex-wrap items-center gap-1 mb-2">
                      <span className="text-xs text-gray-500 mr-1">Equipment source:</span>
                      {CONTAINER_TYPES.map((c) => (
                        <label key={c.value} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 border rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={containers.includes(c.value)}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...containers, c.value]
                                : containers.filter((x) => x !== c.value);
                              setContainers(next);
                            }}
                            className="rounded"
                          />
                          {c.label}
                        </label>
                      ))}
                    </div>
                    {containers.length > 0 && containerCandidates.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1 mb-2">
                        <span className="text-xs text-gray-500 mr-1">Which one:</span>
                        <select
                          value={scopeKey}
                          onChange={(e) => setScopeKey(e.target.value)}
                          className="px-1 py-0.5 border rounded text-xs"
                          aria-label="Specific container scope"
                          data-testid={`container-scope-${lift.id}`}
                        >
                          <option value="">All of the checked kinds</option>
                          {containerCandidates.map((c) => (
                            <option key={c.key} value={c.key}>{c.label}</option>
                          ))}
                        </select>
                        {scopeCandidate && (
                          <span className="text-xs text-blue-700">
                            Showing only gear on {scopeCandidate.label}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <select
                        value={newRole}
                        onChange={(e) => setNewRole(e.target.value as typeof newRole)}
                        className="px-1 py-1 border rounded text-xs"
                        aria-label="Equipment role"
                      >
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <select
                        value={pickAssetId}
                        onChange={(e) => setPickAssetId(e.target.value)}
                        className="flex-1 px-1 py-1 border rounded text-xs"
                        aria-label="Select from equipment"
                        data-testid={`equipment-select-${lift.id}`}
                      >
                        <option value="">— Select from equipment —</option>
                        {filterAssetsByContainers(assets, containers, scopeLabels, scopeRefs).map((a) => {
                          const verdict = verdicts[String(a.id)];
                          const badge = inspectionBadge(inspectionDisplay(verdict, a));
                          // Fail closed: while verdicts are not loaded every
                          // option is DISABLED with an explicit reason — an
                          // API error never makes gear selectable (§8.8 #3).
                          // UAT-014: role/type compatibility is ALSO gated —
                          // gear of the wrong type for the selected role is
                          // disabled with a why+how reason (a harness is never
                          // a lift component), so the one unified picker
                          // presents only usable, role-compatible gear.
                          const typeOk = assetsForRole(newRole, [a]).length === 1;
                          const blocked = !verdictsLoaded || !verdict || !verdict.usable || !typeOk;
                          const reason = !verdictsLoaded || !verdict
                            ? (verdictsError ?? 'inspection status unavailable')
                            : !verdict.usable
                              ? (verdict.reason ?? '')
                              : !typeOk
                                ? `wrong type for role "${newRole}" — this asset is a ${a.type}; pick role-compatible gear or change the role select first`
                                : '';
                          return (
                            <option
                              key={a.id}
                              value={a.id}
                              disabled={blocked}
                              data-reason={reason}
                            >
                              {badge.icon} {a.name} ({a.type}{a.wllLbs != null ? ` · ${a.wllLbs} lb WLL` : ' · WLL unknown'})
                              {blocked && reason ? ` — BLOCKED: ${reason}` : ''}
                            </option>
                          );
                        })}
                      </select>
                      <button
                        type="button"
                        onClick={pickEquipment}
                        className="px-2 py-1 bg-blue-600 text-white rounded text-xs font-semibold hover:bg-blue-700"
                        data-testid={`equipment-pick-${lift.id}`}
                      >
                        Add
                      </button>
                    </div>
                    {pickBlockReason && (
                      <p className="text-xs text-red-700 mt-1" data-testid={`equipment-block-reason-${lift.id}`}>
                        ✗ {pickBlockReason}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* WD-T6 / v1.4 #1 — TRUCK-ANCHOR UTILIZATION: the truck is the
                  plan ANCHOR (capstan winch attaches to it). Rigging details
                  show that the truck's weight can hold the hoisted weight AND
                  what PERCENTAGE of the truck's weight capacity that takes
                  (red >100%, amber >=80% — the §8.8 display convention). */}
              <div className="border border-gray-200 rounded p-3" data-testid={`truck-anchor-${lift.id}`}>
                <p className="text-xs font-semibold text-gray-700 mb-2">
                  Truck Anchor (v1.4) — the capstan winch anchors to the truck
                </p>
                {anchorVehicle == null ? (
                  <p className="text-xs text-gray-500">
                    No anchor truck selected. Pick the truck this winch anchors to in the plan header
                    ("Anchor truck") — the truck's weight is what holds the hoisted weight.
                  </p>
                ) : (() => {
                  const capacityRaw = anchorVehicle.anchorCapacityLbs ?? anchorVehicle.weightLbs;
                  // SOL-WDT6-001 fix: zero/unknown capacity is NOT a pass —
                  // utilizationBand returns 'incomplete' for it and the old
                  // fall-through called that "can hold" (false reassurance).
                  if (capacityRaw == null || capacityRaw <= 0) {
                    return (
                      <p className="text-xs text-amber-700" data-testid={`truck-anchor-no-weights-${lift.id}`}>
                        {anchorVehicle.name} has no usable weight data (vehicle weight or anchor capacity) —
                        the anchor check cannot run. Fix the truck's weight data on the asset before the lift.
                      </p>
                    );
                  }
                  const capacity = capacityRaw;
                  const capacitySource = anchorVehicle.anchorCapacityLbs != null
                    ? 'explicit anchor capacity'
                    : 'vehicle weight';
                  const hoisted = lift.loading.grossLoadLbs ?? 0;
                  const util = utilizationBand(hoisted, capacity);
                  return (
                    <div className="space-y-1 text-sm">
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        <span className="text-gray-600">Anchor truck: <span className="font-semibold text-gray-900">{anchorVehicle.name}</span></span>
                        <span className="text-gray-600">Hoisted weight: <span className="font-semibold text-gray-900">{Math.round(hoisted)} lb</span></span>
                        <span className="text-gray-600">Truck capacity ({capacitySource}): <span className="font-semibold text-gray-900">{Math.round(capacity)} lb</span></span>
                        <span className={`font-semibold ${utilizationColor(util.band)}`} data-testid={`truck-anchor-util-${lift.id}`}>
                          {util.percent != null ? `Truck utilization: ${util.percent.toFixed(0)}%` : 'Truck utilization: n/a'}
                        </span>
                      </div>
                      <p className={`text-xs font-semibold ${utilizationColor(util.band)}`} data-testid={`truck-anchor-verdict-${lift.id}`}>
                        {util.band === 'red'
                          ? 'EXCEEDS truck capacity — the truck’s weight CANNOT hold the hoisted weight. Stop and re-engineer the lift.'
                          : util.band === 'amber'
                            ? 'At or above 80% of truck capacity — have the qualified person confirm before the lift.'
                            : util.band === 'incomplete'
                              ? 'Truck capacity is unknown — the anchor check cannot run. Fix the truck’s weight data before the lift.'
                              : util.band === 'n/a'
                                ? 'Enter the hoisted weight (Gross Load) to run the anchor check.'
                                : 'Truck weight can hold the hoisted weight.'}
                      </p>
                    </div>
                  );
                })()}
              </div>

              {/* Compliance refs */}
              <div>
                <h4 className="font-bold text-gray-900 border-b pb-2 mb-2">COMPLIANCE REFS</h4>
                <div className="flex flex-wrap gap-1 mb-2">
                  {lift.complianceRefs.map((ref) => (
                    <span key={ref} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-mono">
                      {ref}
                    </span>
                  ))}
                </div>
                {!readOnly && (
                  <div className="flex gap-2">
                    <input
                      type="text" placeholder='e.g. A10.48-2023 §4.4.4.1(b)' value={newRef}
                      onChange={(e) => setNewRef(e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                    />
                    <button onClick={addComplianceRef} className="px-2 py-1 bg-gray-100 rounded text-xs hover:bg-gray-200">Add</button>
                  </div>
                )}
              </div>

              {/* Per-lift sign-off */}
              <div className="border border-gray-200 rounded p-3">
                <p className="text-xs font-semibold text-gray-700 mb-2">Per-Lift Sign-Off</p>
                {lift.signOff.reviewedBy ? (
                  <p className="text-sm text-green-700">
                    Reviewed by <strong>{lift.signOff.reviewedBy}</strong>
                    {lift.signOff.at ? ` — ${new Date(lift.signOff.at).toLocaleString()}` : ''}
                  </p>
                ) : readOnly ? (
                  <p className="text-sm text-gray-500">Not reviewed.</p>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text" placeholder="Reviewer name" value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                    <button
                      onClick={signLift}
                      className="px-3 py-1 bg-green-600 text-white rounded text-xs font-semibold hover:bg-green-700 flex items-center gap-1"
                    >
                      <PenLine className="h-3.5 w-3.5" /> Sign
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT: computed panel + diagram (computed ALWAYS fresh — parent recompute) */}
            <div>
              <h4 className="font-bold text-gray-900 border-b pb-2 mb-4">LIFT DIAGRAM</h4>
              {computed && (
                <>
                  {refused ? (
                    <div className="bg-white rounded-lg p-4 mb-4">
                      <h5 className="font-bold text-red-900 mb-3">REFUSED — NO NUMERIC FORCES</h5>
                      <ShownWork working={computed.working} refusals={computed.refusals} status="refused" />
                    </div>
                  ) : (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-testid={`card-forces-summary-${lift.id}`}>
                      <h5 className="font-bold text-blue-900 mb-3">FORCES</h5>
                      {/* Plain-English plan lines first (Brian 2026-09-09: the raw
                          angle/force tiles read as 'a foreign language'). */}
                      <div className="space-y-1 text-sm">
                        <div>Force on top block: <span className="font-bold text-blue-700">{computed.forces.topBlockResultantLbs.toFixed(1)} lb</span></div>
                        <div>Hoist pull: <span className="font-bold text-blue-700">{computed.forces.winchPullLbs.toFixed(1)} lb</span></div>
                        <div>Load line (per part): <span className="font-bold text-blue-700">{computed.forces.linePartTensionLbs.toFixed(1)} lb</span></div>
                        <div>Tag line hold: <span className="font-bold text-blue-700">{computed.forces.tagLineLbs != null ? `${computed.forces.tagLineLbs.toFixed(1)} lb` : '—'}</span></div>
                      </div>
                      {/* Angles + workings behind a closed Show-math toggle */}
                      <button
                        type="button"
                        onClick={() => setShowMath((v) => !v)}
                        className="mt-3 text-sm text-blue-700 underline"
                        aria-expanded={showMath}
                      >
                        {showMath ? 'Hide math' : 'Show math'}
                      </button>
                      {showMath && (
                        <div className="mt-3 border-t pt-3">
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div><span className="text-gray-600">Load Position Angle (θ):</span> <span className="font-semibold">{computed.derivedAngles.loadPositionDeg.toFixed(2)}°</span></div>
                            <div><span className="text-gray-600">Tag Angle:</span> <span className="font-semibold">{computed.derivedAngles.tagAngleDeg != null ? `${computed.derivedAngles.tagAngleDeg.toFixed(2)}°` : '—'}</span></div>
                            <div><span className="text-gray-600">Fleet Angle:</span> <span className="font-semibold">{computed.derivedAngles.fleetAngleDeg != null ? `${computed.derivedAngles.fleetAngleDeg.toFixed(2)}°` : '—'}</span></div>
                            <div><span className="text-gray-600">Sling Angle (α):</span> <span className="font-semibold">{computed.derivedAngles.slingAlphaDeg != null ? `${computed.derivedAngles.slingAlphaDeg.toFixed(2)}°` : '—'}</span></div>
                          </div>
                          {computed.working.length > 0 && (
                            <div className="mt-3">
                              <ShownWork working={computed.working} status="ok" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <LiftDiagramPanel lift={lift} computed={computed} />
                </>
              )}
              {!computed && (
                <p className="text-sm text-gray-500">
                  Fix geometry inputs so the engine can compute this lift (refusals show no numbers).
                </p>
              )}
              {equipmentTable}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
